import { useCallback, useEffect, useState } from "react"
import { Toast } from "@/components/Toast"
import type { ToastInput, ToastItem } from "@/types/toast"

let counter = 0

function nextId(): string {
  counter += 1
  return `toast-${Date.now()}-${counter}`
}

function toItem(input: ToastInput): ToastItem {
  return { id: nextId(), type: input.type ?? "info", message: input.message }
}

interface ToastHostProps {
  /** Server-rendered flash messages hydrated on mount. */
  initialToasts?: ToastInput[]
  /** Overridable so tests don't wait the full display duration. */
  autoDismissMs?: number
}

/**
 * Global toast overlay. Hydrates server flash, exposes an imperative
 * `window.toast.show` API, and listens for `toast:show` CustomEvents so any
 * Turbo/vanilla code can raise a toast without a full page reload.
 */
export function ToastHost({ initialToasts = [], autoDismissMs }: ToastHostProps): JSX.Element | null {
  const [toasts, setToasts] = useState<ToastItem[]>(() => initialToasts.map(toItem))

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const add = useCallback((input: ToastInput) => {
    if (!input?.message) return
    setToasts((prev) => [...prev, toItem(input)])
  }, [])

  useEffect(() => {
    const handler = (event: WindowEventMap["toast:show"]): void => add(event.detail)
    window.addEventListener("toast:show", handler)
    window.toast = { show: add }
    return () => {
      window.removeEventListener("toast:show", handler)
      if (window.toast?.show === add) delete window.toast
    }
  }, [add])

  if (toasts.length === 0) return null

  return (
    <div
      data-testid="toast-host"
      className="pointer-events-none fixed inset-x-4 top-4 z-[60] flex flex-col items-center gap-2 sm:inset-x-auto sm:left-auto sm:right-4 sm:max-w-sm sm:items-end"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} autoDismissMs={autoDismissMs} onDismiss={remove} />
      ))}
    </div>
  )
}
